import { useState, useRef, useEffect } from 'react';
import './MultiSelectFilter.css';

export interface MultiSelectFilterProps {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  className?: string;
  /** Values that are mutually exclusive with all others. Selecting one clears the rest and vice versa. */
  exclusive?: Set<string>;
  /** 'default' (form control, ~38px) | 'compact' (toolbar, 32px). Default 'default'. */
  size?: 'default' | 'compact';
  /** Shown, but not changeable — for a control another choice has already
   *  answered. Kept visible on purpose: hiding it would leave the reader
   *  wondering where the field went. */
  disabled?: boolean;
  /** How many chosen names the closed control shows before it says "N
   *  selected" instead. 2 suits a toolbar filter; a full-width form field has
   *  room for every name, and a count hides what was chosen. */
  maxNamesShown?: number;
}

export function MultiSelectFilter({ label, options, selected, onChange, className = '', exclusive, size = 'default', disabled = false, maxNamesShown = 2 }: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      // Unchecking — just remove it
      onChange(selected.filter(v => v !== value));
    } else if (exclusive?.has(value)) {
      // Selecting an exclusive value — clear everything else
      onChange([value]);
    } else {
      // Selecting a normal value — remove any exclusive values
      const withoutExclusive = exclusive
        ? selected.filter(v => !exclusive.has(v))
        : selected;
      onChange([...withoutExclusive, value]);
    }
  };

  const display = selected.length === 0
    ? label
    : selected.length <= maxNamesShown
      ? selected.map(v => options.find(o => o.value === v)?.label ?? v).join(', ')
      : `${selected.length} selected`;

  return (
    <div className={`multi-select-filter ${className}`} ref={ref}>
      <button
        type="button"
        className={`multi-select-trigger multi-select-trigger-${size}${selected.length > 0 ? ' has-selection' : ''}`}
        disabled={disabled}
        onClick={() => setOpen(!open)}
      >
        <span className="multi-select-text">{display}</span>
        <span className="multi-select-arrow">{open ? '\u25B4' : '\u25BE'}</span>
      </button>
      {open && (
        <div className="multi-select-dropdown">
          {selected.length > 0 && (
            <button
              type="button"
              className="multi-select-clear"
              onClick={() => { onChange([]); setOpen(false); }}
            >
              Clear all
            </button>
          )}
          {options.map(opt => (
            <label key={opt.value} className="multi-select-option">
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
